import {useState} from 'react'
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import {couponService} from '@/services/couponService'
import {bigClose} from '@img/index.js'

export default function CouponForm({carDetails, setApplyCoupon, onRecalculate, defaultCouponCode}) {
    const [couponValue, setCouponValue] = useState(defaultCouponCode || '')
    // The code currently APPLIED to the trip — not the same as what is in the box.
    const [appliedCoupon, setAppliedCoupon] = useState(defaultCouponCode || '')
    const [isApplying, setIsApplying] = useState(false)
    const [isRemoving, setIsRemoving] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')

    const reset = () => { setError(''); setSuccess('') }

    // Take the applied coupon back off the trip and reprice without it. An empty code
    // is "price this with no coupon" as far as onRecalculate is concerned.
    const unapplyCoupon = async () => {
        if (!appliedCoupon) return
        setAppliedCoupon('')
        setIsRemoving(true)
        try {
            await onRecalculate?.('')
        } catch (err) {
            console.error('Coupon removal failed:', err)
        } finally {
            setIsRemoving(false)
        }
    }

    // Editing an applied code drops the discount — a total on screen must never rest on
    // a code that is no longer in the box.
    const changeCouponValue = (next) => {
        setCouponValue(next)
        reset()
        if (appliedCoupon && next.trim().toUpperCase() !== appliedCoupon.trim().toUpperCase()) {
            unapplyCoupon()
        }
    }

    const clearCoupon = () => {
        setCouponValue('')
        reset()
        unapplyCoupon()
    }

    // The code in the box is already live on the trip, so there is nothing left to
    // apply. Editing it unapplies it (see changeCouponValue), which re-enables this.
    const isApplied = !!appliedCoupon
        && couponValue.trim().toUpperCase() === appliedCoupon.trim().toUpperCase()

    const handleSubmit = async (e) => {
        e.preventDefault()
        reset()
        setIsApplying(true)

        const value = couponValue.trim()
        if (!value) { setError('Please enter a coupon code.'); setIsApplying(false); return }

        try {
            const totalAmount = carDetails?.total_price ?? 0
            const couponResult = await couponService.validateCoupon(value, totalAmount)

            if (!couponResult.isValid) {
                setError(couponResult.error || 'Invalid or expired coupon.')
                setIsApplying(false)
                return
            }

            const carsResult = onRecalculate ? await onRecalculate(value) : {success: true}

            if (!carsResult?.success) {
                setError(carsResult?.error || 'Failed to recalculate prices. Please try again.')
                setIsApplying(false)
                return
            }

            setAppliedCoupon(value)
            setSuccess('Coupon applied successfully.')
            setTimeout(() => setApplyCoupon?.(false), 1000)
        } catch {
            setError('Something went wrong. Please try again.')
        } finally {
            setIsApplying(false)
        }
    }

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="relative">
                <input
                    value={couponValue}
                    placeholder="Coupon Code..."
                    onChange={(e) => changeCouponValue(e.target.value)}
                    className="w-full border border-(--border-color) rounded-full px-5 py-3.5 pr-14 outline-none text-base"
                />
                {/* Only while a coupon is actually APPLIED — the button removes a live
                    discount, so there is nothing for it to do next to a code still being
                    typed. (Editing the code unapplies it, which hides this again.) */}
                {isApplied && (
                    <button
                        type="button"
                        aria-label="Remove coupon"
                        onClick={clearCoupon}
                        className="cursor-pointer absolute top-1/2 right-2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full border-0 bg-[#b1fbe3] p-0 hover:opacity-85"
                    >
                        {/* The same cross the modal header's close uses. */}
                        <img src={bigClose} alt="" className="w-3 h-3"/>
                    </button>
                )}
            </div>
            {error && <p className="text-red-500 text-sm mb-0!">{error}</p>}
            {success && <p className="text-(--text-green) text-sm mb-0!">{success}</p>}
            <button type="submit" disabled={isApplying || isRemoving || isApplied}
                    className="cursor-pointer w-full py-[15px] rounded-full bg-(--text-primary) text-white text-base border-0 flex items-center justify-center min-h-[55px] disabled:opacity-50">
                {isApplying ? <Loader color="#fff"/> : 'Apply Coupon'}
            </button>
        </form>
    )
}
